<div class="container">
  <mat-card>
    <form [formGroup]="editClientForm">
      <mat-card-content>
        <!-- First Row: Office, Legal Form, Account No, External ID -->
        <div class="responsive-row">
          <mat-form-field class="flex-24">
            <mat-label>{{ 'labels.inputs.Office' | translate }}</mat-label>
            <mat-select required formControlName="officeId">
              @for (office of officeOptions; track office) {
                <mat-option [value]="office.id">
                  {{ office.name }}
                </mat-option>
              }
            </mat-select>
            @if (editClientForm.controls.officeId.hasError('required')) {
              <mat-error>
                {{ 'labels.inputs.Office' | translate }} {{ 'labels.commons.is' | translate }}
                <strong>{{ 'labels.commons.required' | translate }}</strong>
              </mat-error>
            }
          </mat-form-field>

          <mat-form-field class="flex-24">
            <mat-label>{{ 'labels.inputs.Legal Form' | translate }}</mat-label>
            <mat-select formControlName="legalFormId">
              @for (legalForm of legalFormOptions; track legalForm) {
                <mat-option [value]="legalForm.id">
                  {{ legalForm.value }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field class="flex-24">
            <mat-label>{{ 'labels.inputs.Account No' | translate }}</mat-label>
            <input
              matInput
              formControlName="accountNo"
              placeholder="Account No"
              title="Account No"
              [placeholder]="'labels.inputs.Account No' | translate"
              [title]="'labels.inputs.Account No' | translate"
            />
          </mat-form-field>

          <mat-form-field class="flex-24">
            <mat-label>{{ 'labels.inputs.External Id' | translate }}</mat-label>
            <input
              matInput
              formControlName="externalId"
              placeholder="External Id"
              title="External Id"
              [placeholder]="'labels.inputs.External Id' | translate"
              [title]="'labels.inputs.External Id' | translate"
            />
          </mat-form-field>
        </div>

        <!-- Name fields section -->
        <div class="responsive-row">
          @if (editClientForm.contains('fullname')) {
            <mat-form-field class="flex-48">
              <mat-label>{{
                'labels.inputs.' + getDateLabel(legalFormId, ['Name', 'Entity Name']) | translate
              }}</mat-label>
              <input
                matInput
                required
                formControlName="fullname"
                placeholder="Full Name"
                title="Full Name"
                [placeholder]="'labels.inputs.' + getDateLabel(legalFormId, ['Name', 'Entity Name']) | translate"
                [title]="'labels.inputs.' + getDateLabel(legalFormId, ['Name', 'Entity Name']) | translate"
              />
              @if (editClientForm.controls.fullname.hasError('required')) {
                <mat-error>
                  {{ 'labels.inputs.Client name' | translate }} {{ 'labels.commons.is' | translate }}
                  <strong>{{ 'labels.commons.required' | translate }}</strong>
                </mat-error>
              }
            </mat-form-field>
          }

          @if (editClientForm.contains('firstname')) {
            <mat-form-field class="flex-32">
              <mat-label>{{ 'labels.inputs.First Name' | translate }}</mat-label>
              <input
                matInput
                required
                formControlName="firstname"
                placeholder="First Name"
                title="First Name"
                [placeholder]="'labels.inputs.First Name' | translate"
                [title]="'labels.inputs.First Name' | translate"
              />
              @if (editClientForm.controls.firstname.hasError('required')) {
                <mat-error>
                  {{ 'labels.inputs.Client first name' | translate }} {{ 'labels.commons.is' | translate }}
                  <strong>{{ 'labels.commons.required' | translate }}</strong>
                </mat-error>
              }
            </mat-form-field>
          }

          @if (editClientForm.contains('middlename')) {
            <mat-form-field class="flex-32">
              <mat-label>{{ 'labels.inputs.Middle Name' | translate }}</mat-label>
              <input
                matInput
                formControlName="middlename"
                placeholder="Middle Name"
                title="Middle Name"
                [placeholder]="'labels.inputs.Middle Name' | translate"
                [title]="'labels.inputs.Middle Name' | translate"
              />
            </mat-form-field>
          }

          @if (editClientForm.contains('lastname')) {
            <mat-form-field class="flex-32">
              <mat-label>{{ 'labels.inputs.Last Name' | translate }}</mat-label>
              <input
                matInput
                required
                formControlName="lastname"
                placeholder="Last Name"
                title="Last Name"
                [placeholder]="'labels.inputs.Last Name' | translate"
                [title]="'labels.inputs.Last Name' | translate"
              />
              @if (editClientForm.controls.lastname.hasError('required')) {
                <mat-error>
                  {{ 'labels.inputs.Client last name' | translate }} {{ 'labels.commons.is' | translate }}
                  <strong>{{ 'labels.commons.required' | translate }}</strong>
                </mat-error>
              }
            </mat-form-field>
          }
        </div>

        <mat-divider class="section-divider"></mat-divider>

        <!-- Date of Birth and Gender Row -->
        <div class="responsive-row">
          <mat-form-field class="flex-48" (click)="dateOfBirthDatePicker.open()">
            <mat-label>{{
              'labels.inputs.' + getDateLabel(legalFormId, ['Date of Birth', 'Incorporation Date']) | translate
            }}</mat-label>
            <input
              matInput
              [max]="maxDate"
              [matDatepicker]="dateOfBirthDatePicker"
              formControlName="dateOfBirth"
              placeholder="Date of Birth"
              title="Date of Birth"
              [placeholder]="
                'labels.inputs.' + getDateLabel(legalFormId, ['Date of Birth', 'Incorporation Date']) | translate
              "
              [title]="
                'labels.inputs.' + getDateLabel(legalFormId, ['Date of Birth', 'Incorporation Date']) | translate
              "
            />
            <mat-datepicker-toggle matSuffix [for]="dateOfBirthDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #dateOfBirthDatePicker></mat-datepicker>
          </mat-form-field>

          @if (legalFormId === 1) {
            <mat-form-field class="flex-48">
              <mat-label>{{ 'labels.inputs.Gender' | translate }}</mat-label>
              <mat-select formControlName="genderId">
                @for (gender of genderOptions; track gender) {
                  <mat-option [value]="gender.id">
                    {{ gender.name }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
        </div>

        <!-- Staff and Is Staff Row -->
        <div class="responsive-row align-center">
          <mat-form-field class="flex-48">
            <mat-label>{{ 'labels.inputs.Staff' | translate }}</mat-label>
            <mat-select formControlName="staffId">
              @for (staff of staffOptions; track staff) {
                <mat-option [value]="staff.id">
                  {{ staff.displayName }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>

          @if (legalFormId === 1) {
            <mat-checkbox class="flex-48 margin-v" labelPosition="before" formControlName="isStaff">
              {{ 'labels.inputs.Is staff' | translate }}?
            </mat-checkbox>
          }
        </div>

        @if (editClientForm.contains('clientNonPersonDetails')) {
          <div class="flex-100 layout-row-wrap gap-2percent column-on-small" formGroupName="clientNonPersonDetails">
            <mat-form-field class="flex-48">
              <mat-label>{{ 'labels.inputs.Constitution' | translate }}</mat-label>
              <mat-select formControlName="constitutionId" required>
                @for (constitution of constitutionOptions; track constitution) {
                  <mat-option [value]="constitution.id">
                    {{ constitution.name }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field class="flex-48">
              <mat-label>{{ 'labels.inputs.Main Business Line' | translate }}</mat-label>
              <mat-select formControlName="mainBusinessLineId">
                @for (business of businessLineOptions; track business) {
                  <mat-option [value]="business.id">
                    {{ business.name }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field class="flex-48" (click)="incorpValidityTillDateDatePicker.open()">
              <mat-label>{{ 'labels.inputs.Incorporation Validity Till Date' | translate }}</mat-label>
              <input
                placeholder="Incorporation Validity Till Date"
                title="Incorporation Validity Till Date"
                [placeholder]="'labels.inputs.Incorporation Validity Till Date' | translate"
                [title]="'labels.inputs.Incorporation Validity Till Date' | translate"
                matInput
                [min]="minDate"
                [max]="maxDate"
                [matDatepicker]="incorpValidityTillDateDatePicker"
                formControlName="incorpValidityTillDate"
              />
              <mat-datepicker-toggle matSuffix [for]="incorpValidityTillDateDatePicker"></mat-datepicker-toggle>
              <mat-datepicker #incorpValidityTillDateDatePicker></mat-datepicker>
            </mat-form-field>
            <mat-form-field class="flex-48">
              <mat-label>{{ 'labels.inputs.Incorporation No' | translate }}</mat-label>
              <input
                matInput
                formControlName="incorpNumber"
                placeholder="Incorporation No"
                title="Incorporation No"
                [placeholder]="'labels.inputs.Incorporation No' | translate"
                [title]="'labels.inputs.Incorporation No' | translate"
              />
            </mat-form-field>
            <mat-form-field class="flex-98">
              <mat-label>{{ 'labels.inputs.Remarks' | translate }}</mat-label>
              <textarea
                matInput
                formControlName="remarks"
                cdkTextareaAutosize
                cdkAutosizeMinRows="2"
                placeholder="Remarks"
                title="Remarks"
                [placeholder]="'labels.inputs.Remarks' | translate"
                [title]="'labels.inputs.Remarks' | translate"
              ></textarea>
            </mat-form-field>
          </div>
        }

        <!-- Mobile No and Email Address Row -->
        <div class="responsive-row">
          <mat-form-field class="flex-48">
            <mat-label>{{ 'labels.inputs.Mobile No' | translate }}</mat-label>
            <input
              matInput
              type="text"
              formControlName="mobileNo"
              placeholder="Mobile No"
              title="Mobile No"
              [placeholder]="'labels.inputs.Mobile No' | translate"
              [title]="'labels.inputs.Mobile No' | translate"
            />
          </mat-form-field>

          <mat-form-field class="flex-48">
            <mat-label>{{ 'labels.inputs.Email Address' | translate }}</mat-label>
            <input
              matInput
              formControlName="emailAddress"
              placeholder="Email Address"
              title="Email Address"
              [placeholder]="'labels.inputs.Email Address' | translate"
              [title]="'labels.inputs.Email Address' | translate"
            />
            @if (editClientForm.controls.emailAddress.errors?.email) {
              <mat-error> Email not valid </mat-error>
            }
          </mat-form-field>
        </div>

        <!-- Client Type and Client Classification Row -->
        <div class="responsive-row">
          <mat-form-field class="flex-48">
            <mat-label>{{ 'labels.inputs.Client Type' | translate }}</mat-label>
            <mat-select formControlName="clientTypeId">
              @for (clientType of clientTypeOptions; track clientType) {
                <mat-option [value]="clientType.id">
                  {{ clientType.name }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field class="flex-48">
            <mat-label>{{ 'labels.inputs.Client Classification' | translate }}</mat-label>
            <mat-select formControlName="clientClassificationId">
              @for (clientClassification of clientClassificationTypeOptions; track clientClassification) {
                <mat-option [value]="clientClassification.id">
                  {{ clientClassification.name }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <!-- Submitted On and Activated On Row -->
        <div class="responsive-row">
          <mat-form-field class="flex-48" (click)="submittedOnDatePicker.open()">
            <mat-label>{{ 'labels.inputs.Submitted On' | translate }}</mat-label>
            <input
              placeholder="Submitted On"
              title="Submitted On"
              [placeholder]="'labels.inputs.Submitted On' | translate"
              [title]="'labels.inputs.Submitted On' | translate"
              matInput
              [min]="minDate"
              [max]="maxDate"
              [matDatepicker]="submittedOnDatePicker"
              formControlName="submittedOnDate"
              required
            />
            <mat-datepicker-toggle matSuffix [for]="submittedOnDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #submittedOnDatePicker></mat-datepicker>
            @if (editClientForm.controls.submittedOnDate.hasError('required')) {
              <mat-error>
                {{ 'labels.inputs.Submitted Date' | translate }} {{ 'labels.commons.is' | translate }}
                <strong>{{ 'labels.commons.required' | translate }}</strong>
              </mat-error>
            }
          </mat-form-field>

          <mat-form-field class="flex-48" (click)="activatedOnDatePicker.open()">
            <mat-label>{{ 'labels.inputs.Activated On' | translate }}</mat-label>
            <input
              placeholder="Activated On"
              title="Activated On"
              [placeholder]="'labels.inputs.Activated On' | translate"
              [title]="'labels.inputs.Activated On' | translate"
              matInput
              [min]="editClientForm.value.submittedOnDate"
              [max]="maxDate"
              [matDatepicker]="activatedOnDatePicker"
              formControlName="activationDate"
            />
            <mat-datepicker-toggle matSuffix [for]="activatedOnDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #activatedOnDatePicker></mat-datepicker>
          </mat-form-field>
        </div>
      </mat-card-content>

      <mat-card-actions class="button-container">
        <button type="button" mat-raised-button [routerLink]="['../general']">
          {{ 'labels.buttons.Cancel' | translate }}
        </button>
        <button type="submit" mat-raised-button color="primary" [disabled]="!editClientForm.valid" (click)="submit()">
          {{ 'labels.buttons.Submit' | translate }}
        </button>
      </mat-card-actions>
    </form>
  </mat-card>
</div>
